import React from "react";
import { ReactComponent as LogoIcon } from "@/assets/service-icons/animated/logo.svg";
import { ReactComponent as CampaignIcon } from "@/assets/service-icons/animated/campaign.svg";
import { ReactComponent as EventIcon } from "@/assets/service-icons/animated/event.svg";
import { ReactComponent as BrochureIcon } from "@/assets/service-icons/animated/brochure.svg";
import { ReactComponent as WebsiteIcon } from "@/assets/service-icons/animated/website.svg";
import { ReactComponent as PackagingIcon } from "@/assets/service-icons/animated/packaging.svg";
import { ReactComponent as CubeIcon } from "@/assets/service-icons/animated/3d.svg";
import { ReactComponent as SignageIcon } from "@/assets/service-icons/animated/signage.svg";

/**
 * Homepage "What We Do" service icons (Sep 2026 interaction upgrade).
 *
 * Renders the owner's original SVG artwork INLINE via SVGR so the CSS
 * micro-animations can address real paths/groups. The animated variants
 * in assets/service-icons/animated/ carry only render-neutral classes and
 * <g> wrappers — verified pixel-identical to the supplied originals.
 *
 * Color: source files keep their Illustrator fill (#f26722); the rendered
 * fill is overridden to brand #FF6600 in index.css so the originals stay
 * byte-pristine while the site keeps its approved orange.
 *
 * Unknown icon keys fall back to the approved PNG so a CMS-added finder
 * tile can never render blank.
 */
const ICONS = {
  logo: LogoIcon,
  campaign: CampaignIcon,
  event: EventIcon,
  brochure: BrochureIcon,
  website: WebsiteIcon,
  packaging: PackagingIcon,
  "3d": CubeIcon,
  signage: SignageIcon,
};

// CSS class suffixes — a CSS ident may not start with a digit.
const CLASS_ALIAS = { "3d": "cube" };

// Optical balance (owner directive #1): the seven artworks have slightly
// different content bounds within their 1024 canvas. These minimal scales
// equalize perceived size inside the fixed icon slot. Geometry is never
// modified — this is a display-size adjustment only.
const OPTICAL_SCALE = { brochure: 0.91, packaging: 0.92, "3d": 0.94 };

export const ServiceIcon = ({ iconKey, fallbackSrc, className = "" }) => {
  const Cmp = ICONS[iconKey];
  if (!Cmp) {
    return (
      <img
        src={fallbackSrc}
        alt=""
        className={`${className} object-contain`}
        draggable="false"
        loading="lazy"
        decoding="async"
        aria-hidden="true"
      />
    );
  }
  const scale = OPTICAL_SCALE[iconKey];
  return (
    <Cmp
      className={`ur-svc-icon ur-svc-icon--${CLASS_ALIAS[iconKey] || iconKey} ${className}`}
      style={scale ? { transform: `scale(${scale})` } : undefined}
      aria-hidden="true"
      focusable="false"
    />
  );
};
